/**
 * Hero ability hook contracts for the Legendary Arena game engine.
 *
 * Defines the data-only HeroAbilityHook interface, condition and effect
 * descriptors, and pure query/filter utilities. These contracts are
 * consumed at setup time and observed (read-only) by the rule engine.
 *
 * WP-021 scope: hooks are inert by design — no hero ability executes
 * effects. Execution is deferred to WP-022+.
 *
 * No boardgame.io imports. No registry imports. Contracts only.
 */
/**
 * Returns whether a card matches an investigate criterion list. INCLUSIVE OR: the
 * card matches when it satisfies ANY criterion. An empty list never matches.
 *
 * Pure, deterministic, no I/O. A criterion whose needed card fact is unavailable
 * (undefined) does not match — the caller supplies undefined for a card with no
 * stat / trait entry, so such a card is never drawn on a fabricated 0.
 *
 * @param criteria - The effect's static criteria (OR-combined).
 * @param candidate - The card facts projected from the runtime snapshots.
 * @returns Whether the card satisfies at least one criterion.
 */
export function investigateCandidateMatches(criteria, candidate) {
    for (const criterion of criteria) {
        if (investigateCriterionMatches(criterion, candidate)) {
            return true;
        }
    }
    return false;
}
/**
 * Returns whether a card satisfies a single investigate criterion. A criterion whose
 * needed fact is undefined (no stat / trait entry) does not match.
 *
 * @param criterion - The single criterion to test.
 * @param candidate - The card facts projected from the runtime snapshots.
 * @returns Whether the card satisfies the criterion.
 */
function investigateCriterionMatches(criterion, candidate) {
    if (criterion.kind === 'icon') {
        const value = criterion.icon === 'attack' ? candidate.attack : candidate.recruit;
        return value !== undefined && value > 0;
    }
    if (criterion.kind === 'cost') {
        if (candidate.cost === undefined) {
            return false;
        }
        if (criterion.comparison === 'eq') {
            return candidate.cost === criterion.value;
        }
        if (criterion.comparison === 'lte') {
            return candidate.cost <= criterion.value;
        }
        // why: the remaining comparison is 'gte' (the union has exactly three members).
        return candidate.cost >= criterion.value;
    }
    if (criterion.kind === 'hero-class') {
        // why: WP-703 / D-24523 — a dual-class card matches the criterion on EITHER printed class.
        return candidate.heroClass === criterion.heroClass
            || candidate.heroClass2 === criterion.heroClass;
    }
    // why: the remaining kind is 'team' (the union has exactly four members).
    return candidate.team !== undefined
        && candidate.team !== null
        && candidate.team === criterion.team;
}
// ---------------------------------------------------------------------------
// Query / filter utilities (pure, read-only)
// ---------------------------------------------------------------------------
/**
 * Returns hero ability hooks matching a specific timing value.
 *
 * @param hooks - Array of hero ability hooks to filter.
 * @param timing - Timing value to match.
 * @returns Filtered array of hooks with the specified timing.
 */
export function filterHooksByTiming(hooks, timing) {
    const result = [];
    for (const hook of hooks) {
        if (hook.timing === timing) {
            result.push(hook);
        }
    }
    return result;
}
/**
 * Returns hero ability hooks matching a specific keyword.
 *
 * @param hooks - Array of hero ability hooks to filter.
 * @param keyword - Keyword to match.
 * @returns Filtered array of hooks containing the specified keyword.
 */
export function filterHooksByKeyword(hooks, keyword) {
    const result = [];
    for (const hook of hooks) {
        for (const hookKeyword of hook.keywords) {
            if (hookKeyword === keyword) {
                result.push(hook);
                break;
            }
        }
    }
    return result;
}
/**
 * Returns hero ability hooks for a specific card.
 *
 * @param hooks - Array of hero ability hooks to filter.
 * @param cardId - CardExtId to match.
 * @returns Filtered array of hooks for the specified card.
 */
export function getHooksForCard(hooks, cardId) {
    const result = [];
    for (const hook of hooks) {
        if (hook.cardId === cardId) {
            result.push(hook);
        }
    }
    return result;
}
//# sourceMappingURL=heroAbility.types.js.map